import { Box, Button, Divider, Flex } from "@chakra-ui/react";
import LoanTypeFields from "components/loans/LoanTypeFields";
import BoxHeader from "components/elements/BoxHeader";
import { AlertsContext } from "providers/alerts";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useRef, useState } from "react";
import { apiRequest } from "utils/api";
import { FieldValue, FieldValueMap } from "utils/types";
import { mapLoanValues } from "../../FieldsTable";
import { supabase } from "index";
import { getErrorMessage } from "utils/errorHandler";
import { LoanFieldModel } from "providers/loans/loan-field.model";
import { DrawRequestModel } from "providers/loans/draw-request.motel";
import { FeeModel } from "providers/loans/fee.model";
import { useLoanActions } from "providers/loans/mutations/loan-actions";
import {
  ApplyFilterTo,
  LoanApplicationToogle,
  LoanApplicationToogleOptions,
} from "../loan-application-toogle";

interface LoanFieldsEditProps {
  onSave: () => void;
  onCancel: () => void;
  columns?: number;
}

const LoanFieldsEdit = ({ onSave, onCancel, columns = 2 }: LoanFieldsEditProps) => {
  const { user } = useTenant();
  const { loan, refetch: loadLoan, accountsReceivables, balance } = useContext(LoanContext);
  const { showSuccess, showError } = useContext(AlertsContext);
  const [isLoading, setIsLoading] = useState(false);

  const [fields, setFields] = useState<LoanFieldModel[]>([]);
  const [values, setValues] = useState<any>({});
  const [fieldValues, setFieldValues] = useState<FieldValueMap>({});
  const fieldValuesRef = useRef<FieldValueMap>(fieldValues);
  const [missingFieldIds, setMissingFieldIds] = useState<string[]>([]);

  const { applyChanges } = useLoanActions();

  const custom_loan_headers =
    user?.company?.company_configurations?.[0]?.ui_prefrences?.fields_edit_headers;

  const custom_fields_edit_header =
    user?.company?.company_configurations?.[0]?.ui_prefrences?.fields_edit_headers;

  const setFieldValue = (field_id: string, value: FieldValue) => {
    fieldValuesRef.current = {
      ...fieldValuesRef.current,
      [field_id]: value,
    };
    setFieldValues(fieldValuesRef.current);
  };

  useEffect(() => {
    setValues(mapLoanValues(loan?.values ?? []));
    setFields(loan?.fields ?? []);
  }, [loan]);

  useEffect(() => {
    if (fields.length > 0) {
      fields.forEach((field: any) => {
        setFieldValue(field.id, field.data_key ? (loan as any)[field.data_key] : values[field.id]);
      });
    }
  }, [fields, loan, values]);

  const calculateTLA = async (value: any) => {
    const rwaLoanAmountId = (
      (fields.find((val: any) => val.data_key === "rwa_loan_amount") || {}) as LoanFieldModel
    ).id;
    const participationId = (
      (fields.find((val: any) => val.data_key === "participation_percent") || {}) as LoanFieldModel
    ).id;
    const tlaId = (
      (fields.find((val: any) => val.data_key === "total_loan_amount") || {}) as LoanFieldModel
    ).id;
    const arv3PartyId = (
      (fields.find((val: any) => val.data_key === "arv_3rd_party") || {}) as LoanFieldModel
    ).id;
    const ltaId = (
      (fields.find((val: any) => val.data_key === "loan_to_arv") || {}) as LoanFieldModel
    ).id;

    const balanceAmountId = (
      (fields.find((val: any) => val.identifier_key === "balance_amount") || {}) as LoanFieldModel
    ).id;
    const totalNetLoanAmountId = (
      (fields.find((val: any) => val.identifier_key === "total_net_loan_amount") ||
        {}) as LoanFieldModel
    ).id;

    if (balanceAmountId && totalNetLoanAmountId) {
      try {
        const totalInterestAmount = balance?.interestBalances.totalInterest ?? 0;
        const payments = loan!.draw_requests.filter((draw: any) => draw.status === "paid");

        //todo: check if is the object draw request model here
        const fundedToDate = payments.reduce((fundedAmount: number, payment: DrawRequestModel) => {
          return (fundedAmount += payment.amount);
        }, 0);

        value[balanceAmountId] = (fundedToDate + totalInterestAmount).toFixed(2);

        const feeTotal = loan!.fees?.reduce((feeTotalAmount: number, fee: FeeModel) => {
          return (feeTotalAmount += fee.fee_amount ?? 0);
        }, 0) as number;

        value[totalNetLoanAmountId] = (
          (Number(loan!.amount) ?? 0) -
          totalInterestAmount -
          feeTotal
        ).toFixed(2);
      } catch (error) {
        console.warn("problem calculating fields");
      }
    }

    if (value[participationId] !== null && value[rwaLoanAmountId] !== null) {
      if (
        value[participationId] === "" ||
        isNaN(value[rwaLoanAmountId]) ||
        isNaN(value[participationId])
      )
        value[tlaId] = null;
      else {
        value[tlaId] = value[rwaLoanAmountId] / (value[participationId] / 100);
      }
    }

    if (value[tlaId] !== null && value[arv3PartyId] !== null) {
      if (value[arv3PartyId] === "" || isNaN(value[tlaId]) || isNaN(value[arv3PartyId]))
        value[ltaId] = null;
      else {
        value[ltaId] = ((value[tlaId] / value[arv3PartyId]) * 100).toFixed(2);
      }
    }
  };

  const onSubmit = async () => {
    try {
      setMissingFieldIds(
        fields
          .filter((field: any) => field.required && !fieldValues[field.id])
          .map((field: any) => field.id)
      );

      setIsLoading(true);

      const field_values = fields.map((field: any) => {
        const loanValue = loan!.values.find((val: any) => val.loan_field.id === field.id);
        return {
          ...field,
          value: fieldValues[field?.id],
          loan_value_id: loanValue?.id,
        };
      });

      //data for the loans table to get the amortization schedule
      const interestRateId = (
        (fields.find((val: any) => val.data_key === "custom_interest_rate") || {}) as LoanFieldModel
      ).id;
      const fundingDateId = (
        (fields.find((val: any) => val.data_key === "funding_date") || {}) as LoanFieldModel
      ).id;
      const durationId = (
        (fields.find((val: any) => val.data_key === "duration") || {}) as LoanFieldModel
      ).id;
      const holidayPeriodId = (
        (fields.find((val: any) => val.data_key === "holiday_period") || {}) as LoanFieldModel
      ).id;
      const getEffectiveDateId = (
        (fields.find((val: any) => val.data_key === "effective_date") || {}) as LoanFieldModel
      ).id;

      const interestRate = fieldValues[interestRateId]?.InterestRate;
      const interestLoanType = fieldValues[interestRateId]?.LoanType;
      const funding_date = fieldValues[fundingDateId];
      const getDurationValue = fieldValues[durationId];
      const getHolidayPeriodValue = fieldValues[holidayPeriodId];
      const getEffectiveDate = fieldValues[getEffectiveDateId];

      const customFundedValue = interestLoanType ? true : false;

      await applyChanges.mutateAsync({
        id: loan!.id,
        fields: field_values,
        values: values,
        interest_rate_: interestRate,
        interest_loan_type_: interestLoanType,
        funding_date_: funding_date,
        duration_: getDurationValue,
        holiday_period_: getHolidayPeriodValue,
        effective_date: getEffectiveDate,
        funded_value_: customFundedValue,
      });

      for (const payment of accountsReceivables) {
        await apiRequest("/supabase/accounts_receivable/update", {
          id: payment.id,
          paymentAmount: payment.payment_amount,
          paymentStatus: payment.payment_status,
          paymentDate: payment.payment_date,
        });
      }

      showSuccess({
        title: "Loan Updated",
        description: "The loan details have been successfully saved.",
      });

      await loadLoan();

      onSave();
    } catch (err) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState<LoanApplicationToogleOptions>(
    LoanApplicationToogleOptions.Application
  );

  const notUnderwritingFields = fields?.filter((field: any) => !field.underwriting);
  const underwritingFields = fields?.filter((field: any) => field.underwriting);

  const applicationTerm =
    user?.lender_company?.id === "2e6eae2b-de55-4c49-96c6-ff77740565f1" ||
    user?.lender_company?.id === "59d63802-e33d-4b99-9ddd-e92004455eb3"
      ? "Term Sheet"
      : "Application";

  return (
    <Flex direction="column" gap={4}>
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <BoxHeader title="Loan Details" mb="0px" />
        <Flex gap={2}>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button isLoading={isLoading} size="sm" colorScheme="primary" onClick={onSubmit}>
            Save Changes
          </Button>
        </Flex>
      </Flex>

      <LoanApplicationToogle
        activeTab={activeTab}
        onChange={setActiveTab}
        applicationTerm={applicationTerm}
      />

      {/* Not Underwriting Fields */}
      <LoanTypeFields
        columns={columns}
        fields={notUnderwritingFields.filter(field => ApplyFilterTo(user.type, activeTab, field))}
        values={fieldValues}
        setValue={setFieldValue}
        missingFieldIds={missingFieldIds}
        calculateTLA={calculateTLA}
        // onClose={() => {}}
      />

      {/* Underwriting Fields */}
      {user.type === "lender" && underwritingFields.length > 0 && (
        <Box mt={8}>
          <Divider my="20px" />
          <BoxHeader
            mb="0px"
            title={custom_loan_headers ? `${custom_fields_edit_header}` : "Underwriting Fields"}
          />
          <Divider mt="10px" mb="20px" />
          <LoanTypeFields
            columns={columns}
            fields={underwritingFields}
            values={fieldValues}
            setValue={setFieldValue}
            missingFieldIds={missingFieldIds}
            calculateTLA={calculateTLA}
            // onClose={() => {}}
          />
        </Box>
      )}
    </Flex>
  );
};

export default LoanFieldsEdit;
